import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/contexts/AuthContext';
import {
  Loader2, ShieldAlert, Flame, ImageIcon, Link2, Fingerprint,
  MessageSquare, User, Ban, Check, X, AlertTriangle, Activity,
  ArrowLeft, ExternalLink, Filter,
} from 'lucide-react';
import BackNavButton from '@/components/BackNavButton';
import { toast } from 'sonner';

const CATEGORY_COLORS = {
  romance_scam:            { bg: '#FEF2F2', fg: '#B91C1C', label: 'Romance Scam' },
  phishing:                { bg: '#FEF2F2', fg: '#B91C1C', label: 'Phishing' },
  grooming:                { bg: '#FEF2F2', fg: '#B91C1C', label: 'Grooming' },
  coercion:                { bg: '#FEF2F2', fg: '#B91C1C', label: 'Coercion' },
  'sexual/minors':         { bg: '#FEF2F2', fg: '#7F1D1D', label: 'CSAM Alert' },
  'hate/threatening':      { bg: '#FEF2F2', fg: '#B91C1C', label: 'Hate/Threat' },
  'harassment/threatening':{ bg: '#FEF2F2', fg: '#B91C1C', label: 'Harassment/Threat' },
  possible_minor:          { bg: '#FDF7EC', fg: '#B38138', label: 'Possible Minor' },
  linked_account:          { bg: '#EFF6FF', fg: '#1E40AF', label: 'Linked Account' },
  ai_generated:            { bg: '#F5F3FF', fg: '#6D28D9', label: 'AI-Generated' },
  scam:                    { bg: '#FEF2F2', fg: '#B91C1C', label: 'Scam' },
};

function relativeTime(iso) {
  if (!iso) return '';
  const sec = Math.floor((new Date() - new Date(iso)) / 1000);
  if (sec < 60) return 'now';
  if (sec < 3600) return `${Math.floor(sec/60)}m`;
  if (sec < 86400) return `${Math.floor(sec/3600)}h`;
  if (sec < 604800) return `${Math.floor(sec/86400)}d`;
  return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function CategoryBadge({ category }) {
  const cfg = CATEGORY_COLORS[category] || { bg: '#F3F4F6', fg: '#374151', label: category };
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border"
      style={{ backgroundColor: cfg.bg, color: cfg.fg, borderColor: `${cfg.fg}33` }}
      data-testid={`cat-badge-${category}`}>
      {cfg.label}
    </span>
  );
}

// ==================== OVERVIEW TAB ====================
function OverviewTab({ overview, onSelectCategory, loading }) {
  if (loading || !overview) return <div className="p-8 text-center text-[#8A8A93]"><Loader2 className="w-6 h-6 animate-spin mx-auto"/></div>;
  const kpis = [
    { icon: ShieldAlert, label: 'Open alerts',       value: overview.open_alerts,       color: '#B91C1C', testid: 'kpi-open-alerts' },
    { icon: Flame,       label: 'Alerts / 24h',      value: overview.alerts_24h,        color: '#DC2626', testid: 'kpi-alerts-24h' },
    { icon: X,           label: 'Text blocks / 24h', value: overview.blocks_24h,        color: '#B91C1C', testid: 'kpi-blocks-24h' },
    { icon: AlertTriangle, label: 'Warns / 24h',     value: overview.warns_24h,         color: '#B38138', testid: 'kpi-warns-24h' },
    { icon: ImageIcon,   label: 'Photo blocks / 24h',value: overview.photo_blocks_24h,  color: '#6D28D9', testid: 'kpi-photo-blocks-24h' },
    { icon: Fingerprint, label: 'Fingerprinted users',value: overview.fingerprint_users,color: '#1E40AF', testid: 'kpi-fingerprint-users' },
  ];
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
        {kpis.map(k => {
          const Icon = k.icon;
          return (
            <div key={k.label} data-testid={k.testid} className="rounded-2xl border bg-white p-4" style={{borderColor:'var(--hairline-strong)'}}>
              <div className="flex items-center gap-2 mb-2">
                <span className="w-8 h-8 rounded-lg flex items-center justify-center" style={{backgroundColor:`${k.color}15`}}>
                  <Icon size={16} strokeWidth={1.8} color={k.color}/>
                </span>
                <span className="text-[10px] font-bold uppercase tracking-wider text-[#8A8A93]">{k.label}</span>
              </div>
              <p className="font-display text-[26px] font-[600] text-[#0F0F10] tabular-nums leading-none">{k.value ?? 0}</p>
            </div>
          );
        })}
      </div>
      <div className="rounded-2xl border bg-white p-5" style={{borderColor:'var(--hairline-strong)'}}>
        <p className="text-[10px] font-bold uppercase tracking-[1.4px] text-[#8A8A93] mb-3">OPEN ALERTS BY CATEGORY</p>
        {overview.by_category?.length === 0 ? (
          <p className="text-[13px] text-[#4B4B52] py-4 text-center">Nothing open. Nice.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {overview.by_category?.map(c => (
              <button key={c.category}
                data-testid={`overview-cat-${c.category}`}
                onClick={() => onSelectCategory(c.category)}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border hover:bg-black/[0.02] transition-colors"
                style={{borderColor:'var(--hairline-strong)'}}>
                <CategoryBadge category={c.category}/>
                <span className="text-[12px] font-bold tabular-nums text-[#0F0F10]">{c.count}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== ALERT LIST ROW ====================
function AlertRow({ alert, onOpenLinked, onResolve, onDismiss, onBan }) {
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);

  const wrap = async (fn) => {
    setBusy(true);
    try { await fn(); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border bg-white overflow-hidden" style={{borderColor:'var(--hairline-strong)'}}
         data-testid={`alert-row-${alert.alert_id}`}>
      <button onClick={() => setExpanded(v => !v)} className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-black/[0.015] transition-colors">
        <img
          src={alert.user?.photo_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${alert.user?.user_id || 'x'}`}
          alt="" className="w-9 h-9 rounded-full object-cover bg-black/[0.05] flex-shrink-0"
          onError={e => { e.target.src = `https://api.dicebear.com/7.x/avataaars/svg?seed=${alert.user?.user_id || 'x'}`; }}/>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <CategoryBadge category={alert.category}/>
            <p className="text-[11px] text-[#8A8A93] tabular-nums">{relativeTime(alert.created_at)}</p>
          </div>
          <p className="text-[13px] font-semibold text-[#0F0F10] truncate">{alert.subject}</p>
          <p className="text-[11px] text-[#4B4B52] truncate">{alert.user?.email || alert.user?.display_name || alert.user_id}</p>
        </div>
      </button>
      {expanded && (
        <div className="border-t px-4 py-3 bg-[#FAFAFA]" style={{borderColor:'var(--hairline)'}}>
          {alert.body && (
            <div className="mb-3">
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#8A8A93] mb-1">Detected content</p>
              <div className="rounded-lg bg-white border p-3 text-[12px] text-[#0F0F10] whitespace-pre-wrap break-words" style={{borderColor:'var(--hairline)'}}>
                {alert.body.slice(0, 800)}{alert.body.length > 800 && '…'}
              </div>
            </div>
          )}
          {alert.related && Object.keys(alert.related).length > 0 && (
            <div className="mb-3 text-[11px] text-[#4B4B52]">
              <span className="font-semibold">Details:</span>{' '}
              {Object.entries(alert.related).map(([k,v]) => (
                <span key={k} className="mr-2">{k}=<span className="font-mono">{JSON.stringify(v)}</span></span>
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <button data-testid={`alert-linked-${alert.alert_id}`} onClick={() => onOpenLinked(alert.user?.user_id)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider bg-white border hover:bg-black/[0.03] transition"
              style={{borderColor:'var(--hairline-strong)'}}>
              <Link2 size={11} strokeWidth={2.4}/> Linked accounts
            </button>
            {alert.status === 'open' && (
              <>
                <button data-testid={`alert-resolve-${alert.alert_id}`} disabled={busy}
                  onClick={() => wrap(() => onResolve(alert.alert_id))}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider text-white bg-[#15803D] hover:opacity-90 transition disabled:opacity-50">
                  <Check size={11} strokeWidth={2.4}/> Resolve
                </button>
                <button data-testid={`alert-dismiss-${alert.alert_id}`} disabled={busy}
                  onClick={() => wrap(() => onDismiss(alert.alert_id))}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider bg-white border hover:bg-black/[0.03] transition disabled:opacity-50"
                  style={{borderColor:'var(--hairline-strong)'}}>
                  <X size={11} strokeWidth={2.4}/> Dismiss (false positive)
                </button>
                <button data-testid={`alert-ban-${alert.alert_id}`} disabled={busy}
                  onClick={() => wrap(() => onBan(alert.user?.user_id, alert.category, false))}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider text-white bg-[#B91C1C] hover:opacity-90 transition disabled:opacity-50">
                  <Ban size={11} strokeWidth={2.4}/> Ban user
                </button>
                <button data-testid={`alert-ban-cascade-${alert.alert_id}`} disabled={busy}
                  onClick={() => wrap(() => onBan(alert.user?.user_id, alert.category, true))}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider text-white bg-[#7F1D1D] hover:opacity-90 transition disabled:opacity-50">
                  <Ban size={11} strokeWidth={2.4}/> Ban + cascade linked
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== ALERTS TAB ====================
function AlertsTab({ statusFilter, setStatusFilter, categoryFilter, setCategoryFilter, alerts, loading, onOpenLinked, onResolve, onDismiss, onBan }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {['open', 'resolved', 'dismissed', 'all'].map(s => (
          <button key={s} data-testid={`filter-status-${s}`}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider transition
              ${statusFilter === s ? 'bg-[#0F0F10] text-white' : 'bg-white border text-[#4B4B52] hover:bg-black/[0.03]'}`}
            style={statusFilter !== s ? {borderColor:'var(--hairline-strong)'} : {}}>
            {s}
          </button>
        ))}
        {categoryFilter && (
          <button data-testid="clear-cat-filter"
            onClick={() => setCategoryFilter(null)}
            className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider bg-[#B91C1C] text-white">
            <X size={11}/> Category: {categoryFilter}
          </button>
        )}
      </div>
      {loading ? (
        <div className="p-8 text-center text-[#8A8A93]"><Loader2 className="w-6 h-6 animate-spin mx-auto"/></div>
      ) : alerts.length === 0 ? (
        <div className="p-8 text-center text-[#8A8A93] rounded-2xl border bg-white" style={{borderColor:'var(--hairline)'}}
             data-testid="alerts-empty">
          <ShieldAlert size={28} className="mx-auto mb-2 text-[#C4C4CB]"/>
          <p className="text-[13px]">No {statusFilter !== 'all' && statusFilter} alerts. All quiet.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="alerts-list">
          {alerts.map(a => (
            <AlertRow key={a.alert_id} alert={a}
              onOpenLinked={onOpenLinked}
              onResolve={onResolve}
              onDismiss={onDismiss}
              onBan={onBan}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ==================== LINKED-ACCOUNTS DRAWER ====================
function LinkedDrawer({ userId, onClose, onBan }) {
  const [loading, setLoading] = useState(true);
  const [linked, setLinked] = useState([]);
  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    api.get(`/admin/moderation/user/${userId}/linked`)
      .then(r => setLinked(r.data?.linked || []))
      .catch(() => toast.error('Failed to load linked accounts'))
      .finally(() => setLoading(false));
  }, [userId]);
  return (
    <div className="fixed inset-0 z-50 bg-black/40" onClick={onClose} data-testid="linked-drawer">
      <div className="absolute right-0 top-0 bottom-0 w-full sm:w-[440px] bg-white shadow-xl overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center gap-3" style={{borderColor:'var(--hairline)'}}>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-black/[0.04] flex items-center justify-center" data-testid="linked-close">
            <ArrowLeft size={16}/>
          </button>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[1.4px] text-[#8A8A93]">LINKED ACCOUNTS</p>
            <p className="font-display text-[15px] font-[600] text-[#0F0F10]">User <span className="font-mono text-[13px]">{userId?.slice(-8)}</span></p>
          </div>
        </div>
        <div className="p-4">
          {loading ? (
            <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#8A8A93]"/></div>
          ) : linked.length === 0 ? (
            <p className="text-[13px] text-[#4B4B52] text-center py-8">No linked accounts. This user is likely a solo actor.</p>
          ) : (
            <div className="space-y-2">
              {linked.map(l => (
                <div key={l.user_id} className="rounded-xl border bg-white p-3 flex items-start gap-3"
                     style={{borderColor:'var(--hairline-strong)'}} data-testid={`linked-${l.user_id}`}>
                  <img src={l.user?.photo_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${l.user_id}`}
                       alt="" className="w-9 h-9 rounded-full object-cover bg-black/[0.05] flex-shrink-0"/>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1 mb-1 flex-wrap">
                      <span className="text-[12px] font-bold text-[#0F0F10]">{l.user?.display_name || l.user?.name || l.user_id.slice(-8)}</span>
                      <span className="px-1.5 py-0.5 rounded-full bg-[#EFF6FF] text-[#1E40AF] text-[10px] font-bold">
                        Score {l.score.toFixed(1)}
                      </span>
                      {l.user?.status === 'banned' && (
                        <span className="px-1.5 py-0.5 rounded-full bg-[#B91C1C] text-white text-[10px] font-bold">BANNED</span>
                      )}
                    </div>
                    <p className="text-[11px] text-[#4B4B52] truncate">{l.user?.email}</p>
                    <p className="text-[10px] text-[#8A8A93] mt-1">{l.reasons.join(' · ')}</p>
                  </div>
                  {l.user?.status !== 'banned' && (
                    <button data-testid={`linked-ban-${l.user_id}`}
                      onClick={() => onBan(l.user_id, 'manual (linked)', false)}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider text-white bg-[#B91C1C] hover:opacity-90 flex-shrink-0">
                      <Ban size={9}/> Ban
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ==================== SAFETY LOG TAB ====================
function SafetyLogTab({ items, loading, filters, setFilters }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {['all', 'block', 'warn', 'log'].map(a => (
          <button key={a} data-testid={`filter-log-action-${a}`}
            onClick={() => setFilters(f => ({ ...f, action: a === 'all' ? null : a }))}
            className={`px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider transition
              ${(filters.action === a || (a === 'all' && !filters.action)) ? 'bg-[#0F0F10] text-white' : 'bg-white border text-[#4B4B52] hover:bg-black/[0.03]'}`}
            style={{borderColor:'var(--hairline-strong)'}}>
            {a}
          </button>
        ))}
      </div>
      {loading ? <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#8A8A93]"/></div>
       : items.length === 0 ? <div className="p-8 text-center text-[#8A8A93]">No safety log entries.</div>
       : (
        <div className="space-y-2" data-testid="safety-log-list">
          {items.map((it, i) => (
            <div key={i} className="rounded-xl border bg-white p-3" style={{borderColor:'var(--hairline-strong)'}}>
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                  it.action === 'block' ? 'bg-[#FEF2F2] text-[#B91C1C]' :
                  it.action === 'warn'  ? 'bg-[#FDF7EC] text-[#B38138]' :
                                          'bg-black/[0.05] text-[#4B4B52]'
                }`}>{it.action}</span>
                <span className="text-[11px] text-[#4B4B52]">{it.kind}</span>
                <span className="text-[11px] text-[#8A8A93] ml-auto tabular-nums">{relativeTime(it.created_at)}</span>
              </div>
              <p className="text-[12px] text-[#0F0F10] font-mono truncate">{it.text_preview || it.reason || '(no preview)'}</p>
              {it.categories && Object.keys(it.categories).length > 0 && (
                <div className="mt-2 flex gap-1 flex-wrap">
                  {Object.entries(it.categories).filter(([_,m]) => m?.present).map(([n, m]) => (
                    <span key={n} className="text-[9px] font-bold uppercase tracking-wider text-[#4B4B52] bg-black/[0.05] px-1.5 py-0.5 rounded">
                      {n} {Math.round(m.confidence)}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ==================== MAIN PAGE ====================
export default function AdminModeration() {
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);
  const [tab, setTab] = useState('overview');   // 'overview' | 'alerts' | 'safety-log' | 'photo-log'
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('open');
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [logItems, setLogItems] = useState([]);
  const [logLoading, setLogLoading] = useState(true);
  const [logFilters, setLogFilters] = useState({ action: null });
  const [linkedUserId, setLinkedUserId] = useState(null);

  // Auth gate
  useEffect(() => {
    api.get('/admin/me')
      .then(() => setAuthChecked(true))
      .catch(() => navigate('/', { replace: true }));
  }, [navigate]);

  const loadOverview = useCallback(() => {
    setOverviewLoading(true);
    api.get('/admin/moderation/overview')
      .then(r => setOverview(r.data))
      .catch(() => toast.error('Failed to load overview'))
      .finally(() => setOverviewLoading(false));
  }, []);

  const loadAlerts = useCallback(() => {
    setAlertsLoading(true);
    const params = new URLSearchParams();
    if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
    if (categoryFilter) params.set('category', categoryFilter);
    api.get(`/admin/moderation/alerts?${params.toString()}`)
      .then(r => setAlerts(r.data?.items || []))
      .catch(() => toast.error('Failed to load alerts'))
      .finally(() => setAlertsLoading(false));
  }, [statusFilter, categoryFilter]);

  const loadLog = useCallback(() => {
    setLogLoading(true);
    const params = new URLSearchParams();
    if (logFilters.action) params.set('action', logFilters.action);
    api.get(`/admin/moderation/safety-log?${params.toString()}`)
      .then(r => setLogItems(r.data?.items || []))
      .catch(() => toast.error('Failed to load log'))
      .finally(() => setLogLoading(false));
  }, [logFilters]);

  useEffect(() => { if (authChecked) loadOverview(); }, [authChecked, loadOverview]);
  useEffect(() => { if (authChecked && tab === 'alerts') loadAlerts(); }, [authChecked, tab, loadAlerts]);
  useEffect(() => { if (authChecked && tab === 'safety-log') loadLog(); }, [authChecked, tab, loadLog]);

  const resolveAlert = async (alertId) => {
    try {
      await api.post(`/admin/moderation/alerts/${alertId}/action`, { action: 'resolve' });
      toast.success('Resolved');
      loadAlerts();
      loadOverview();
    } catch { toast.error('Action failed'); }
  };
  const dismissAlert = async (alertId) => {
    try {
      await api.post(`/admin/moderation/alerts/${alertId}/action`, { action: 'dismiss', note: 'false positive' });
      toast.success('Dismissed');
      loadAlerts();
      loadOverview();
    } catch { toast.error('Action failed'); }
  };
  const banUser = async (userId, category, cascade) => {
    if (!userId) return;
    if (!window.confirm(cascade ? `Ban this user AND all linked accounts for "${category}"?` : `Ban this user for "${category}"?`)) return;
    try {
      const r = await api.post(`/admin/moderation/user/${userId}/ban`,
        { reason: category, cascade_linked: cascade });
      toast.success(`Banned ${r.data?.banned_user_ids?.length || 1} account(s)`);
      loadAlerts();
      loadOverview();
    } catch (e) { toast.error(e.response?.data?.detail || 'Ban failed'); }
  };

  if (!authChecked) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#B91C1C]"/></div>;
  }

  const TABS = [
    { id: 'overview',    label: 'Overview',   icon: Activity,   testid: 'tab-overview' },
    { id: 'alerts',      label: 'Alerts',     icon: ShieldAlert, testid: 'tab-alerts' },
    { id: 'safety-log',  label: 'Safety Log', icon: MessageSquare, testid: 'tab-safety-log' },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-24" data-testid="admin-moderation-page">
      <div className="max-w-4xl mx-auto px-4 pt-6">
        <div className="flex items-center gap-3 mb-6">
          <BackNavButton to="/admin/support"/>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[1.4px] text-[#8A8A93]">TRUST &amp; SAFETY</p>
            <h1 className="font-display text-[24px] font-[600] text-[#0F0F10] leading-tight">Moderation</h1>
          </div>
        </div>

        {/* Tab bar */}
        <div className="flex items-center gap-1 mb-6 border-b" style={{borderColor:'var(--hairline)'}}>
          {TABS.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.id} data-testid={t.testid} onClick={() => setTab(t.id)}
                className={`inline-flex items-center gap-1.5 px-4 py-3 text-[12px] font-bold uppercase tracking-wider transition border-b-2 ${
                  tab === t.id ? 'text-[#0F0F10] border-[#B91C1C]' : 'text-[#8A8A93] border-transparent hover:text-[#4B4B52]'
                }`}>
                <Icon size={13} strokeWidth={2}/>
                {t.label}
              </button>
            );
          })}
        </div>

        {tab === 'overview' && (
          <OverviewTab overview={overview} loading={overviewLoading}
            onSelectCategory={cat => { setCategoryFilter(cat); setTab('alerts'); }}/>
        )}
        {tab === 'alerts' && (
          <AlertsTab
            statusFilter={statusFilter} setStatusFilter={setStatusFilter}
            categoryFilter={categoryFilter} setCategoryFilter={setCategoryFilter}
            alerts={alerts} loading={alertsLoading}
            onOpenLinked={setLinkedUserId}
            onResolve={resolveAlert}
            onDismiss={dismissAlert}
            onBan={banUser}/>
        )}
        {tab === 'safety-log' && (
          <SafetyLogTab items={logItems} loading={logLoading} filters={logFilters} setFilters={setLogFilters}/>
        )}
      </div>

      {linkedUserId && (
        <LinkedDrawer userId={linkedUserId} onClose={() => setLinkedUserId(null)} onBan={banUser}/>
      )}
    </div>
  );
}
